import React, { useState, useEffect, useRef } from 'react';
import { X, Paperclip, MoreVertical, Trash2, Loader2, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { API_BASE_URL } from '../api/config';
import { sessionManager } from '../api/sessionManager';

export default function MailModal({ target, onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState('');
  const [sending, setSending] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const [recipient, setRecipient] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionBoxRef = useRef(null);

  const draftKey = `mail_draft_${target?.id || target?.application_id || target?.applicant_id || 'unknown'}`;

  // Fetch suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      const user = sessionManager.getUser();
      if (!user?.id) return;
      try {
        const res = await fetch(`${API_BASE_URL}/user_recipients.php?user_id=${user.id}`);
        const data = await res.json();
        if (data.success) {
          setSuggestions(data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch recipient suggestions', err);
      }
    };
    fetchSuggestions();
  }, []);

  useEffect(() => {
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const { subject: dSub, message: dMsg, recipient: dRec } = JSON.parse(draft);
        if (dSub) setSubject(dSub);
        if (dMsg) setMessage(dMsg);
        if (dRec) setRecipient(dRec);
      } catch (e) {}
    }
  }, [draftKey]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionBoxRef.current && !suggestionBoxRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  const handleDeleteSuggestion = async (e, emailToRemove) => {
    e.stopPropagation();
    const user = sessionManager.getUser();
    if (!user?.id) return;

    try {
      const res = await fetch(`${API_BASE_URL}/user_recipients.php`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, email: emailToRemove })
      });
      const data = await res.json();
      if (data.success) {
        setSuggestions(prev => prev.filter(email => email !== emailToRemove));
      }
    } catch (err) {
      console.error('Failed to delete suggestion', err);
    }
  };

  const handleSend = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!recipient || !emailRegex.test(recipient)) {
      showToast('Please enter a valid email address in the Recipients field.', 'error');
      return;
    }

    const user = sessionManager.getUser();
    const userId = user?.id;
    const role = user?.role || 'employee';

    if (!userId) {
      showToast('Your session has expired. Please log in again.', 'error');
      return;
    }

    setSending(true);
    try {
      const payload = {
        to: recipient,
        subject,
        message,
        customer_id: target?.id || target?.application_id || null,
        user_id: userId,
        role: role,
        attachments: attachments.map(a => ({
          name: a.name,
          data: a.data
        }))
      };

      const res = await fetch(`${API_BASE_URL}/send_email.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      
      if (data.success) {
        showToast('Email sent successfully!', 'success');
        localStorage.removeItem(draftKey);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        if (data.error_type === 'MISSING_SMTP_CONFIG') {
          showToast('Failed to send email: Your SMTP settings are not configured. Click the Settings gear icon at the top right to configure them.', 'error');
        } else {
          showToast('Failed to send email: ' + (data.message || 'Unknown error'), 'error');
        }
      }
    } catch (err) {
      showToast('Network error while sending email.', 'error');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleFileClick = (type) => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAttachments(prev => [
          ...prev, 
          {
            name: `${uploadType ? uploadType + '_' : ''}${file.name}`,
            data: ev.target.result,
            type: file.type,
            previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
          }
        ]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (subject || message || recipient) {
      localStorage.setItem(draftKey, JSON.stringify({ subject, message, recipient }));
    }
    onClose();
  };

  const handleDiscard = () => {
    localStorage.removeItem(draftKey);
    onClose();
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
    setIsMaximized(false);
  };

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    setIsMinimized(false);
  };

  const filteredSuggestions = suggestions.filter(email => email.toLowerCase().includes(recipient.toLowerCase()));

  if (isMinimized) {
    return (
      <div className="fixed bottom-0 right-10 z-[100] w-[300px] bg-white rounded-t-xl shadow-lg border border-gray-200">
        <div className="bg-[#f2f6fc] text-gray-800 px-4 py-2.5 flex items-center justify-between rounded-t-xl cursor-pointer" onClick={handleMinimize}>
          <h3 className="font-semibold text-[13px] truncate">New Message</h3>
          <div className="flex items-center gap-2 text-gray-500">
            <button onClick={(e) => { e.stopPropagation(); handleMaximize(); }} className="hover:bg-gray-200 p-1 rounded transition-colors"><Maximize2 size={12} /></button>
            <button onClick={(e) => { e.stopPropagation(); handleClose(); }} className="hover:bg-gray-200 p-1 rounded transition-colors"><X size={14} /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4`}>
      <input type="file" ref={fileInputRef} className="hidden" style={{ display: 'none' }} onChange={handleFileChange} />
      
      {toast && (
        <div className={`fixed top-10 left-1/2 transform -translate-x-1/2 z-[300] max-w-sm w-11/12 px-4 py-3 rounded-xl shadow-2xl flex items-center justify-between gap-3 text-[13px] font-medium border animate-in slide-in-from-top-4 duration-300 ${
          toast.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
            : 'bg-rose-50 border-rose-200 text-rose-800'
        }`}>
          <span className="flex-1">{toast.message}</span>
          <button type="button" onClick={() => setToast(null)} className="opacity-60 hover:opacity-100 transition-opacity">
            <X size={14} />
          </button>
        </div>
      )}

      <div 
        className={`bg-white rounded-t-xl rounded-b-xl shadow-2xl flex flex-col overflow-visible animate-in fade-in duration-200 transition-all ${
          isMaximized ? 'w-full h-full fixed inset-0 m-0 rounded-none' : 'w-full max-w-[600px] h-[500px]'
        }`}
      >
        
        {/* Header */}
        <div className="bg-[#f2f6fc] text-gray-800 px-4 py-2.5 flex items-center justify-between rounded-t-xl" onDoubleClick={handleMaximize}>
          <h3 className="font-semibold text-[13px]">New Message</h3>
          <div className="flex items-center gap-2 text-gray-500">
            <button onClick={handleMinimize} className="hover:bg-gray-200 p-1 rounded transition-colors">
              <Minus size={14} />
            </button>
            <button onClick={handleMaximize} className="hover:bg-gray-200 p-1 rounded transition-colors">
              {isMaximized ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
            </button>
            <button onClick={handleClose} className="hover:bg-gray-200 p-1 rounded transition-colors"><X size={14} /></button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="px-4 z-[110]">
          <div className="flex items-center border-b border-gray-100 py-2 relative" ref={suggestionBoxRef}>
            <span className="text-gray-500 text-[13px] w-20">Recipients</span>
            <input 
              type="text" 
              className="flex-1 outline-none text-[13px] text-gray-800" 
              value={recipient}
              onChange={(e) => {
                setRecipient(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Enter recipient email address"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute top-[100%] left-20 right-0 bg-white border border-gray-200 shadow-xl rounded-lg mt-1 max-h-48 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-1">
                {filteredSuggestions.map((email) => (
                  <div 
                    key={email}
                    className="px-3 py-2 text-[13px] text-gray-800 hover:bg-gray-50 cursor-pointer flex items-center justify-between group"
                    onClick={() => {
                      setRecipient(email);
                      setShowSuggestions(false);
                    }}
                  >
                    <span>{email}</span>
                    <button 
                      onClick={(e) => handleDeleteSuggestion(e, email)}
                      className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                      title="Remove suggestion"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center border-b border-gray-100 py-2">
            <span className="text-gray-500 text-[13px] w-20">Subject</span>
            <input 
              type="text" 
              className="flex-1 outline-none text-[13px] text-gray-800" 
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
        </div>

        {/* Text Area */}
        <div className="flex-1 p-4 flex flex-col relative overflow-hidden">
          <textarea
            className="flex-1 w-full outline-none resize-none text-[13px] text-gray-800"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
          
          {/* Attachments Preview Tray */}
          {attachments.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap gap-3 overflow-y-auto max-h-[100px]">
              {attachments.map((att, idx) => (
                <div key={idx} className="relative group border border-gray-200 rounded-md overflow-hidden bg-gray-50 flex items-center justify-center w-20 h-20">
                  {att.previewUrl ? (
                    <img src={att.previewUrl} alt="preview" className="object-cover w-full h-full" />
                  ) : (
                    <div className="flex flex-col items-center p-2 text-xs text-gray-500 truncate w-full text-center">
                      <Paperclip size={16} className="mb-1" />
                      <span className="truncate w-full">{att.name}</span>
                    </div>
                  )}
                  <button 
                    onClick={() => removeAttachment(idx)}
                    className="absolute top-1 right-1 bg-white hover:bg-red-500 hover:text-white text-gray-700 p-1 rounded-full shadow-md transition-colors"
                    title="Remove attachment"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-3 flex items-center justify-between bg-white border-t border-gray-50">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleSend} 
              disabled={sending}
              className="px-5 py-2 bg-[#0b57d0] text-white text-[13px] font-medium rounded-full shadow-sm hover:bg-[#0842a0] transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : 'Send'}
            </button>

            {/* Custom Pan / Aadhaar Buttons */}
            <div className="flex items-center gap-2">
              <button onClick={() => handleFileClick('PAN')} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 rounded-md hover:bg-gray-200 transition-colors">
                Pan
              </button>
              <button onClick={() => handleFileClick('Aadhaar')} className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 rounded-md hover:bg-gray-200 transition-colors">
                Aadhaar
              </button>
            </div>

            {/* General Attachment icon */}
            <div className="flex items-center gap-3 text-gray-500 ml-2">
              <Paperclip size={18} onClick={() => handleFileClick('')} className="cursor-pointer hover:text-gray-700 transform rotate-45" title="Attach file" />
            </div>
          </div>

          <div className="flex items-center gap-3 text-gray-500">
            <MoreVertical size={18} className="cursor-pointer hover:text-gray-700" />
            <Trash2 size={18} onClick={handleDiscard} className="cursor-pointer hover:text-gray-700" />
          </div>
        </div>

      </div>
    </div>
  );
}
